import { useState, useMemo } from 'react';
import { BackToTopPill } from "@/components/BackToTopPill";
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Search, Package, Edit, Trash2, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { useSupplierMutations } from '@/features/suppliers/hooks/useSupplierMutations';
import { extractCountry, parseEmails } from '@/features/suppliers/types';
import type { Supplier } from '@/features/suppliers/types';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar } from '@/components/layout/PageActionBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageErrorState } from '@/components/shared/PageErrorState';
import { CatalogSuppliersCard } from '@/features/supplier-catalog/components/CatalogSuppliersCard';
import { CatalogLinkedPill } from '@/features/supplier-catalog/components/CatalogLinkedPill';
import { PoKindBadge } from '@/features/purchase-orders/components/PoKindBadge';
import { useOwnedFloorIds } from '@/features/inventory/hooks/useOwnedFloorIds';

type SortField = 'title' | 'contact_name' | 'email' | 'phone' | 'country' | 'orders' | 'spent' | 'status';
type SortDirection = 'asc' | 'desc';

export default function Suppliers() {
  const navigate = useNavigate();
  const { format: formatCurrency } = useCurrencyFormatter();
  // BLU-1506: the Order and wait mark only means something to an org that runs a floor.
  const runsAFloor = useOwnedFloorIds().length > 0;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { suppliers, isLoading, error } = useSuppliers({ includeInactive: true });
  const { deactivateSupplier, reactivateSupplier } = useSupplierMutations();

  usePageTitle('Suppliers', 'Manage your suppliers and their contact details');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...suppliers];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((s) =>
        s.title.toLowerCase().includes(query) ||
        s.contact_name.toLowerCase().includes(query) ||
        s.email?.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'title':
          return dir * a.title.localeCompare(b.title);
        case 'contact_name':
          return dir * (a.contact_name || '').localeCompare(b.contact_name || '');
        case 'email':
          return dir * (a.email || '').localeCompare(b.email || '');
        case 'phone':
          return dir * (a.phone || '').localeCompare(b.phone || '');
        case 'country': {
          const countryA = extractCountry(a.address);
          const countryB = extractCountry(b.address);
          return dir * countryA.localeCompare(countryB);
        }
        case 'orders':
          return dir * ((a.order_count ?? 0) - (b.order_count ?? 0));
        case 'spent':
          return dir * ((a.total_spent ?? 0) - (b.total_spent ?? 0));
        case 'status':
          return dir * (Number(b.is_active) - Number(a.is_active));
        default:
          return 0;
      }
    });

    return result;
  }, [suppliers, searchQuery, sortField, sortDirection]);

  const handleEdit = (e: React.MouseEvent, supplier: Supplier) => {
    e.stopPropagation();
    navigate(`/suppliers/${supplier.id}`);
  };

  const handleDelete = (e: React.MouseEvent, supplier: Supplier) => {
    e.stopPropagation();
    setSelectedSupplier(supplier);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedSupplier) return;
    deactivateSupplier.mutate(
      { supplierId: selectedSupplier.id },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSelectedSupplier(null);
        },
      }
    );
  };

  const handleReactivate = (e: React.MouseEvent, supplier: Supplier) => {
    e.stopPropagation();
    reactivateSupplier.mutate({ supplierId: supplier.id });
  };

  const handleAddNew = () => {
    navigate('/suppliers/new');
  };

  const handleRowClick = (supplier: Supplier) => {
    navigate(`/suppliers/${supplier.id}`);
  };

  const colCount = 9;

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        <PageActionBar
          ariaLabel="Suppliers actions"
          left={
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search suppliers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-9 text-xs"
              />
            </div>
          }
          right={
            <Button size="sm" className="h-8 px-3 text-xs gap-1.5" onClick={handleAddNew}>
              <Plus className="h-3.5 w-3.5" />
              Add supplier
            </Button>
          }
        />

        {/* BLU-1268: the PrintRun catalog suppliers, above the org's own. */}
        <CatalogSuppliersCard />

        {/* Table */}
        <div className="rounded-md border mt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('title')}>
                    Name <SortIcon field="title" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('contact_name')}>
                    Contact <SortIcon field="contact_name" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('email')}>
                    Email <SortIcon field="email" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('phone')}>
                    Phone <SortIcon field="phone" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('country')}>
                    Country <SortIcon field="country" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('orders')}>
                    # Orders <SortIcon field="orders" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('spent')}>
                    Total Spent <SortIcon field="spent" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('status')}>
                    Status <SortIcon field="status" />
                  </button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                      Loading suppliers...
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="p-0">
                    <PageErrorState
                      title="Couldn't load suppliers"
                      description={error.message}
                    />
                  </TableCell>
                </TableRow>
              ) : filteredAndSorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="p-0">
                    {searchQuery ? (
                      <EmptyState
                        icon={Package}
                        title={`No suppliers matching "${searchQuery}"`}
                        description="Try a different search term."
                        action={
                          <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
                            Clear search
                          </Button>
                        }
                      />
                    ) : (
                      <EmptyState
                        icon={Package}
                        title="No suppliers yet"
                        description="Add your first supplier to get started."
                        action={
                          <Button variant="outline" size="sm" onClick={handleAddNew}>
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            Add supplier
                          </Button>
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSorted.map((supplier) => {
                  const emails = parseEmails(supplier.email);
                  const country = extractCountry(supplier.address);
                  return (
                    <TableRow
                      key={supplier.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(supplier)}
                    >
                      <TableCell className="font-medium">
                        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                          <Link
                            to={`/suppliers/${supplier.id}`}
                            className="hover:underline"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {supplier.title}
                          </Link>
                          {supplier.catalogSupplierId && <CatalogLinkedPill />}
                          {/* BLU-1506: the same mark its POs wear; Buy on print day is unmarked */}
                          {runsAFloor && supplier.supplyMode === 'order_and_wait' && <PoKindBadge kind="order_and_wait" />}
                        </span>
                      </TableCell>
                      <TableCell>{supplier.contact_name || '-'}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[200px]">
                        {emails.length > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="truncate">{emails[0]}</span>
                            {emails.length > 1 && (
                              <span className="text-muted-foreground text-[10px]">
                                +{emails.length - 1} more
                              </span>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{supplier.phone || '-'}</TableCell>
                      <TableCell>{country || '-'}</TableCell>
                      <TableCell>{supplier.order_count ?? 0}</TableCell>
                      <TableCell>{formatCurrency(supplier.total_spent ?? 0)}</TableCell>
                      <TableCell>
                        <Badge variant={supplier.is_active ? 'success' : 'secondary'}>
                          {supplier.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => handleEdit(e, supplier)}
                            disabled={!supplier.is_active}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {supplier.is_active ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={(e) => handleDelete(e, supplier)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                              onClick={(e) => handleReactivate(e, supplier)}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Deactivate Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate "{selectedSupplier?.title}". It will be removed from the supplier
              dropdown but can be reactivated later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deactivate Supplier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BackToTopPill />
    </div>
  );
}
